import { Button } from "@datapay-by-datacom/design-system";
import type { VectorKbKnowledgeSource } from "../types";
import {
  formatCategoryLabel,
  groupKnowledgeSourceOptions,
  knowledgeSourceOptionValue,
  parseKnowledgeSourceOptionValue,
  type CatalogOrgRef,
} from "../utils/catalogDisplay";
import styles from "./AgentCatalog.module.css";

type OrgSource = {
  id: string;
  category: VectorKbKnowledgeSource["category"];
};

/**
 * Repeating organisation picker shared by every binding whose config names a
 * set of orgs. Callers supply the option list, so a binding that only supports
 * some categories filters them out before they can be chosen.
 */
export function OrgSourcesEditor({
  idPrefix,
  label,
  sources,
  options,
  maxSources,
  addLabel,
  hint,
  onChange,
}: {
  idPrefix: string;
  label: string;
  sources: OrgSource[];
  options: CatalogOrgRef[];
  maxSources: number;
  addLabel: string;
  hint: string;
  onChange: (sources: OrgSource[]) => void;
}) {
  const grouped = groupKnowledgeSourceOptions(options);

  const updateRow = (index: number, patch: OrgSource) => {
    onChange(sources.map((row, i) => (i === index ? patch : row)));
  };

  return (
    <div>
      <label>{label}</label>
      {sources.map((source, index) => {
        const selectedValue = source.id
          ? knowledgeSourceOptionValue(source)
          : "";
        const selectedInList = options.some(
          (option) =>
            option.id === source.id && option.category === source.category,
        );

        return (
          <div key={index} className={styles.knowledgeSourceRow}>
            <div className={styles.fieldRow}>
              <label htmlFor={`${idPrefix}-kb-${index}`}>Organisation</label>
              <div className={styles.sourceSelect}>
                <select
                  id={`${idPrefix}-kb-${index}`}
                  className={styles.select}
                  value={selectedValue}
                  onChange={(e) => {
                    const next = parseKnowledgeSourceOptionValue(
                      e.target.value,
                    );
                    updateRow(index, next ?? { id: "", category: "internal" });
                  }}
                >
                  <option value="">Select organisation…</option>
                  {source.id && !selectedInList && (
                    <option value={selectedValue}>Unknown organisation</option>
                  )}
                  {grouped.map((group) => (
                    <optgroup
                      key={group.category}
                      label={formatCategoryLabel(group.category)}
                    >
                      {group.orgs.map((org) => (
                        <option
                          key={knowledgeSourceOptionValue(org)}
                          value={knowledgeSourceOptionValue(org)}
                        >
                          {org.name}
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </select>
                {source.id && (
                  <span className={styles.badge}>
                    {formatCategoryLabel(source.category)}
                  </span>
                )}
              </div>
            </div>
            <Button
              variant="secondary"
              size="small"
              onPress={() => onChange(sources.filter((_, i) => i !== index))}
              isDisabled={sources.length <= 1}
            >
              Remove
            </Button>
          </div>
        );
      })}
      <Button
        variant="secondary"
        size="small"
        onPress={() => onChange([...sources, { id: "", category: "internal" }])}
        isDisabled={sources.length >= maxSources}
      >
        {addLabel}
      </Button>
      <p className={styles.fieldHint}>{hint}</p>
    </div>
  );
}
